import type { RouteLocationNormalized, RouteLocationRaw } from "vue-router";
import { isUuid } from "@/shared/utils/alias";

/*
  An Operator URL names its dataspace, and its process, by an alias or by an id
  (`/operator/night-shift/processes/intake`, `/operator/3f2c…/processes/9a71…`), and everything below
  the route — the API, the dataspace's room, the seat, the stores — works with ids alone. So the route
  turns a segment into an id ONCE, here, and the views read the ids it found (`useOperatorIds`).

  - A segment shaped like an id is one (`isUuid`): an alias never has that shape.
  - Any other segment is an alias, and is looked up — in what is known first, else by asking the
    server. One that names nothing (a `404`, which is also what an alias of a dataspace the caller
    cannot reach is) stands for an id as it is, so the page that follows is refused as for an
    unknown id today: no page of its own for it.
  - A URL that does not say what is known — an id where an alias is known, an alias spelled
    otherwise, an alias that was removed — is redirected to the one that does, its query and its
    hash kept. Not `replace`: a redirect keeps its navigation's own, so the URL that was asked for is
    never in the history and Back still returns to the page before.
*/

/** The ids a route names: what everything below the route works with. */
export interface OperatorIds {
	dataspaceId: string;
	processId?: string;
}

declare module "vue-router" {
	interface RouteMeta {
		/** What `operatorIdsGuard` made of a route's `:dataspaceId` and `:processId`. */
		operatorIds?: OperatorIds;
	}
}

/** What the route asks of what is known about aliases — `operatorAliasStore` in the app. */
export interface AliasDirectory {
	/** The alias the dataspace has: `null` for none, `undefined` while nothing has said. */
	dataspaceAlias(id: string): string | null | undefined;
	processAlias(dataspaceId: string, processId: string): string | null | undefined;
	/** The id an alias names, or `null` when it names none. */
	resolveDataspace(segment: string): Promise<string | null>;
	resolveProcess(dataspaceId: string, segment: string): Promise<string | null>;
}

function segmentOf(param: unknown): string | undefined {
	const value = Array.isArray(param) ? param[0] : param;
	return typeof value === "string" && value ? value : undefined;
}

/**
 * The segment a URL should name a thing by: its alias when it has one, its id when it has none, and
 * whatever the URL says while nothing is known — so a page opened by its id waits for its row.
 */
function canonical(segment: string, id: string, known: string | null | undefined): string {
	if (known === undefined) return segment;
	return known ?? id;
}

/**
 * The segments a route naming `ids` should have, given what `directory` knows; `null` when it has
 * them already. The guard asks this as a route is entered, and `useCanonicalOperatorUrl` again
 * whenever what is known changes under an open one: a row read for a page opened by its id, or an
 * alias renamed or removed in another session.
 */
export function canonicalSegments(
	segments: { dataspace: string; process?: string },
	ids: OperatorIds,
	directory: Pick<AliasDirectory, "dataspaceAlias" | "processAlias">,
): { dataspaceId: string; processId?: string } | null {
	const dataspace = canonical(
		segments.dataspace,
		ids.dataspaceId,
		directory.dataspaceAlias(ids.dataspaceId),
	);
	const process =
		segments.process === undefined || ids.processId === undefined
			? undefined
			: canonical(
					segments.process,
					ids.processId,
					directory.processAlias(ids.dataspaceId, ids.processId),
				);
	if (dataspace === segments.dataspace && process === segments.process) return null;
	return process === undefined
		? { dataspaceId: dataspace }
		: { dataspaceId: dataspace, processId: process };
}

/** The ids `route` names — the guard's, or, on a route it did not see, its segments as they are. */
export function operatorIdsOf(
	route: Pick<RouteLocationNormalized, "params" | "meta">,
): Partial<OperatorIds> {
	return (
		route.meta.operatorIds ?? {
			dataspaceId: segmentOf(route.params.dataspaceId),
			processId: segmentOf(route.params.processId),
		}
	);
}

/** The segments of a route as they are, or `null` for one that names no dataspace. */
export function segmentsOf(
	route: Pick<RouteLocationNormalized, "params">,
): { dataspace: string; process?: string } | null {
	const dataspace = segmentOf(route.params.dataspaceId);
	return dataspace ? { dataspace, process: segmentOf(route.params.processId) } : null;
}

/**
 * The guard: for every Operator route with a `:dataspaceId`, the ids its segments name, put on
 * `to.meta`, or the redirect to the URL that says what is known. Any other route is let through —
 * the control center's `:dataspaceId` among them, which is always an id.
 */
export function operatorIdsGuard(directory: AliasDirectory) {
	return async (to: RouteLocationNormalized): Promise<true | RouteLocationRaw> => {
		const segments = segmentsOf(to);
		if (!segments || !to.matched.some((record) => record.meta.section === "operator"))
			return true;

		const dataspaceId = isUuid(segments.dataspace)
			? segments.dataspace
			: await directory.resolveDataspace(segments.dataspace);
		let processId: string | null | undefined;
		if (segments.process !== undefined) {
			processId = isUuid(segments.process)
				? segments.process
				: // A process's alias is its dataspace's: there is nothing to look it up in without one.
					dataspaceId && (await directory.resolveProcess(dataspaceId, segments.process));
		}
		// What names nothing stands for an id as it is, and is refused downstream as one.
		const ids: OperatorIds = {
			dataspaceId: dataspaceId ?? segments.dataspace,
			...(segments.process !== undefined ? { processId: processId || segments.process } : {}),
		};

		const wanted = canonicalSegments(segments, ids, directory);
		if (wanted && to.name) {
			return {
				name: to.name,
				params: { ...to.params, ...wanted },
				query: to.query,
				hash: to.hash,
			};
		}
		to.meta.operatorIds = ids;
		return true;
	};
}
